import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "src/store";

import { usePolicyContentUpdate } from "@dashboard/policies/hooks/usePolicyContentUpdate/usePolicyContentUpdate";
import {
    CollapsibleProvider,
    CollapsibleSectionDetails,
    CollapsibleSectionSummary,
    CollapsibleSectionTitle,
} from "@dashboard/shared/components/CollapsibleSection";
import PolicyCommandsTable from "@dashboard/shared/components/PolicyCommands/PolicyCommandsTable";
import { NewCommandDialog } from "@devices/components/Commands/NewCommandDialog";
import { selectDevicesByGroupIds } from "@devices/store/selectors/devices";
import { MdmPolicyCommand } from "@devices/types";
import { Box } from "@mui/material";
import { SecondaryButton } from "@shared/CustomButton";

import { useStyles } from "./CommandsSection.style";

type Props = {
    policy: API.PolicyViewModel | null;
    readOnly?: boolean;
};

export const CommandsSection = ({ policy, readOnly = false }: Props) => {
    const [isAddCommandDialogOpen, setIsAddCommandDialogOpen] = React.useState(false);

    const { t } = useTranslation(["pages", "common"]);
    const { classes } = useStyles();

    const { updateContent, isLoading } = usePolicyContentUpdate();

    const content = policy?.content ?? [];
    const groupIds = policy?.groupIds ?? [];

    const policyGroupDevices = useSelector((state) => selectDevicesByGroupIds(state, groupIds));

    const onUpdate = (updatedContent: MdmPolicyCommand[]) =>
        policy ? updateContent({ id: policy.id, name: policy.name, content: updatedContent }) : Promise.resolve(false);

    const appendCommandAndSave = async (json: Record<string, unknown>) => {
        await onUpdate([...content, json as MdmPolicyCommand]);
    };

    return (
        <CollapsibleProvider initialValue disabled={!policy}>
            <CollapsibleSectionSummary
                action={
                    <SecondaryButton
                        startIcon={<i className="fa-solid fa-plus" />}
                        onClick={() => setIsAddCommandDialogOpen(true)}
                        disabled={!policy || readOnly}
                        tooltipProps={{
                            title: readOnly
                                ? t("common:tooltip.cannotManagePolicies")
                                : t("pages:policiesPage.tooltip.noPolicySelected"),
                            hide: !!policy && !readOnly,
                        }}
                    >
                        {t("pages:policiesPage.button.addRule")}
                    </SecondaryButton>
                }
            >
                <CollapsibleSectionTitle>{t("pages:policiesPage.section.rules.title")}</CollapsibleSectionTitle>
            </CollapsibleSectionSummary>
            <CollapsibleSectionDetails>
                <Box className={content.length ? classes.tableWrapper : classes.tableWrapperEmpty}>
                    <PolicyCommandsTable
                        content={content}
                        onUpdate={readOnly ? undefined : onUpdate}
                        loading={isLoading}
                        readOnly={readOnly}
                    />
                </Box>
            </CollapsibleSectionDetails>
            {policy ? (
                <NewCommandDialog
                    title={t("pages:policiesPage.button.addRule")}
                    isPolicy
                    devices={policyGroupDevices}
                    isOpen={isAddCommandDialogOpen}
                    toggleDialog={() => setIsAddCommandDialogOpen((prev) => !prev)}
                    createCommand={appendCommandAndSave}
                    loading={isLoading}
                />
            ) : null}
        </CollapsibleProvider>
    );
};
